/**
 * String utility functions for naming conventions
 */

/**
 * Convert kebab-case to PascalCase
 * @example toPascalCase('word-counter') => 'WordCounter'
 * @example toPascalCase('ai-text-generator') => 'AiTextGenerator'
 */
export function toPascalCase(str: string): string {
    return str
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
}

/**
 * Comprehensive route-to-filename mappings for routes that don't follow standard patterns
 * These mappings handle cases where SEO-friendly routes differ from actual file names
 */
const SPECIAL_ROUTE_MAPPINGS: Record<string, string> = {
    'english-dictionary': 'DictionaryPage',
    'thesaurus-synonyms-finder': 'ThesaurusPage',
    'global-public-holidays-finder': 'HolidaysFinderPage',
    'book-finder-search': 'BookFinderPage',
    // Batch 1 - Security
    'password-breach-checker': 'PasswordBreachCheckerPage',
    'email-breach-checker': 'EmailBreachCheckerPage',
    'fake-person-generator': 'FakePersonGeneratorPage',
    // Batch 1 - Explore
    'food-barcode-scanner': 'FoodBarcodeScannerPage',
    // Batch 2 - Earth & Space
    'air-quality-checker': 'AirQualityCheckerPage',
    // Batch 2 - Explore
    'country-comparison-tool': 'CountryComparisonPage',
    // Batch 3 - Explore
    'grammar-checker': 'GrammarCheckerPage',
    'age-calculator-tool': 'AgeCalculatorPage',
    'aspect-ratio-calculator-tool': 'AspectRatioCalculatorPage',
    'barcode-generator-tool': 'BarcodeGeneratorPage',
    'base64-encoder-decoder': 'Base64EncoderPage',
    'bcrypt-hash-generator': 'BcryptGeneratorPage',
    'binary-to-text-translator': 'BinaryToTextPage',
    'bitwise-calculator-tool': 'BitwiseCalculatorPage',
    'bmi-calculator-tool': 'BmiCalculatorPage',
    'bpm-tapper': 'BPMTapperPage',
    'calorie-calculator-tool': 'CalorieCalculatorPage',
    'case-converter': 'CaseConverterPage',
    'chinese-zodiac-calculator': 'ChineseZodiacCalculatorPage',
    'chmod-calculator': 'ChmodCalculatorPage',
    'coin-flip-simulator': 'CoinFlipPage',
    'color-format-converter': 'ColorConverterPage',
    'color-palette-generator': 'ColorPaletteGeneratorPage',
    'cooking-converter': 'CookingConverterPage',
    'cron-expression-generator': 'CronGeneratorPage',
    'css-box-shadow-generator': 'BoxShadowGeneratorPage',
    'css-cubic-bezier-generator': 'CssCubicBezierGenPage',
    'css-clip-path-maker': 'ClipPathMakerPage',
    'css-flexbox-generator': 'FlexboxGeneratorPage',
    'css-gradient-generator': 'GradientGeneratorPage',
    'css-keyframe-generator': 'CssKeyframeGenPage',
    'css-minifier-compressor': 'CssMinifierPage',
    'css-unit-converter-tool': 'CssUnitConverterPage',
    'csv-xml-converter': 'CsvXmlConverterPage',
    'currency-exchange-converter': 'CurrencyConverterPage',
    'dead-pixel-checker': 'DeadPixelCheckerPage',
    'day-of-year-calculator': 'DayOfYearCalculatorPage',
    'date-difference-calculator': 'DateDifferenceCalculatorPage',
    'device-pixel-ratio-checker': 'DevicePixelRatioPage',
    'dice-roller-simulator': 'DiceRollerPage',
    'discount-calculator-tool': 'DiscountCalculatorPage',
    'dns-propagation-checker': 'DnsCheckerPage',
    'dummy-data-generator-tool': 'DummyDataGeneratorPage',
    'electricity-cost-calculator': 'ElectricityCostCalculatorPage',
    'email-extractor-tool': 'EmailExtractorPage',
    'emi-loan-calculator': 'EmiCalculatorPage',
    'epoch-timestamp-converter': 'EpochConverterPage',
    'gamepad-tester': 'GamepadTesterPage',
    'gamepad-tester-tool': 'GamepadTesterPage',
    'gcd-lcm-calculator': 'GcdLcmCalculatorPage',
    'gcd-lcm-calculator-tool': 'GcdLcmCalculatorPage',
    'glassmorphism-css-generator': 'GlassmorphismGeneratorPage',
    'golden-ratio-calculator': 'GoldenRatioCalculatorPage',
    'gpa-calculator-tool': 'GpaCalculatorPage',
    'hash-generator-tool': 'HashGeneratorPage',
    'htaccess-file-generator': 'HtaccessGeneratorPage',
    'html-entity-encoder-decoder': 'HtmlEntityEncoderPage',
    'html-minifier-compressor': 'HtmlMinifierPage',
    'image-color-picker': 'ColorPickerPage',
    'image-compression-tool': 'ImageCompressorPage',
    'image-resizer-tool': 'ImageResizerPage',
    'social-media-image-resizer': 'SocialMediaResizerPage',
    'image-to-base64-converter': 'ImageToBase64Page',
    'image-to-text-ocr': 'ImageToTextPage',
    'image-watermark-beautifier': 'ImageWatermarkPage',
    'ip-geolocation-lookup': 'IpGeolocationPage',
    'ipv4-subnet-calculator': 'SubnetCalculatorPage',
    'javascript-keycode-info': 'KeycodeInfoPage',
    'javascript-minifier-compressor': 'JsMinifierPage',
    'js-code-obfuscator': 'CodeObfuscatorPage',
    'json-path-finder-tool': 'JsonPathFinderPage',
    'json-to-toon-converter': 'JsonFormatterPage',
    'json-validator-tool': 'JsonValidatorPage',
    'jwt-token-decoder': 'JwtDecoderPage',
    'keyboard-tester': 'KeyboardTesterPage',
    'keyboard-tester-tool': 'KeyboardTesterPage',
    'keyboard-tester-test': 'KeyboardTesterPage',
    'keyword-density-analyzer': 'KeywordDensityPage',
    'link-extractor-tool': 'LinkExtractorPage',
    'lorem-ipsum-paragraph-generator': 'LoremIpsumParagraphsPage',
    'lorem-picsum-generator': 'LoremPicsumPage',
    'mac-address-lookup': 'MacLookupPage',
    'markdown-preview-editor': 'MarkdownPreviewPage',
    'markdown-table-generator-tool': 'MarkdownTableGeneratorPage',
    'matrix-calculator': 'MatrixCalculatorPage',
    'matrix-calculator-tool': 'MatrixCalculatorPage',
    'mean-median-mode-calculator': 'MeanMedianModePage',
    'mic-tester': 'MicTesterPage',
    'mic-tester-tool': 'MicTesterPage',
    'morse-code-translator-tool': 'MorseCodeTranslatorPage',
    'my-ip-address-checker': 'IpCheckerPage',
    'number-base-converter-tool': 'NumberBaseConverterPage',
    'online-countdown-timer': 'CountdownTimerPage',
    'online-screen-recorder': 'ScreenRecorderPage',
    'online-signature-pad': 'SignaturePadPage',
    'online-stopwatch-timer': 'StopwatchPage',
    'online-voice-recorder': 'AudioRecorderPage',
    'open-graph-tag-generator': 'OpenGraphGeneratorPage',
    'open-port-scanner': 'PortScannerPage',
    'password-strength-tester-tool': 'PasswordStrengthTesterPage',
    'percentage-calculator-tool': 'PercentageCalculatorPage',
    'percentage-change-calculator': 'PercentageChangeCalculatorPage',
    'phone-number-formatter': 'PhoneFormatterPage',
    'pomodoro-focus-timer': 'PomodoroTimerPage',
    'ppi-calculator': 'PpiCalculatorPage',
    'ppi-calculator-tool': 'PpiCalculatorPage',
    'pregnancy-due-date-calculator': 'PregnancyCalculatorPage',
    'prime-factorizer-tool': 'PrimeFactorizerPage',
    'qr-code-generator-tool': 'QrGeneratorPage',
    'qr-code-scanner-tool': 'QrCodeScannerPage',
    'quadratic-equation-solver': 'QuadraticSolverPage',
    'random-name-picker-tool': 'RandomNamePickerPage',
    'random-sentence-generator': 'RandomSentencePage',
    'reaction-time-test': 'ReactionTimePage',
    'react-component-generator': 'ReactGeneratorPage',
    'readability-checker-tool': 'ReadabilityCheckerPage',
    'regex-tester-debugger': 'RegexTesterPage',
    'retro-ps2-style-filter': 'RetroPs2FilterPage',
    'moon-phase-calculator': 'MoonPhaseCalculatorPage',
    'robots-txt-file-generator': 'RobotsTxtGeneratorPage',
    'robots-txt-tester': 'RobotsTxtGeneratorPage',
    'scientific-calculator-online': 'ScientificCalculatorPage',
    'screen-resolution-checker-tool': 'ScreenResolutionCheckerPage',
    'secure-password-generator': 'PasswordGeneratorPage',
    'sensor-tester': 'SensorTesterPage',
    'serp-snippet-preview': 'SerpPreviewPage',
    'simple-budget-calculator': 'SimpleBudgetPage',
    'slug-to-text-converter': 'SlugToTextPage',
    'spongebob-mocking-meme-generator': 'SpongeBobMemePage',
    'sql-query-formatter': 'SqlFormatterPage',
    'ssl-tls-certificate-checker': 'SslCheckerPage',
    'inflation-calculator-tool': 'InflationCalculatorPage',
    'standard-deviation-calculator': 'StandardDeviationPage',
    'string-encoder-decoder': 'StringEncoderPage',
    'subtitle-converter-tool': 'SubtitleConverterPage',
    'tailwind-config-generator': 'TailwindConfigGenPage',
    'text-cleaner-tool': 'TextCleanerPage',
    'text-diff-checker-tool': 'TextDiffPage',
    'text-encryption-tool': 'TextEncryptionPage',
    'text-list-sorter': 'TextSorterPage',
    'text-statistics-analyzer': 'TextStatisticsPage',
    'text-to-speech-converter': 'TextToSpeechPage',
    'time-card-calculator': 'TimeCardCalculatorPage',
    'unix-timestamp-converter': 'TimestampConverterPage',
    'url-encoder-decoder': 'UrlEncoderPage',
    'url-slug-generator': 'SlugGeneratorPage',
    'user-agent-string-parser': 'UserAgentParserPage',
    'utm-link-builder': 'UtmLinkBuilderPage',
    'uuid-v4-generator': 'UuidGeneratorPage',
    'vibration-tester': 'VibrationTesterPage',
    'webcam-capture-tool': 'WebcamCapturePage',
    'whois-domain-lookup': 'WhoisLookupPage',
    'wifi-qr-code-generator': 'WifiQrGeneratorPage',
    'window-size-checker': 'WindowSizePage',
    'week-number-calculator': 'WeekNumberCalculatorPage',
    'work-days-calculator': 'WorkDaysCalculatorPage',
    'wpm-typing-speed-test': 'TypingSpeedTestPage',
    'xpath-tester': 'XPathTesterPage',
    'xml-sitemap-generator': 'SitemapGeneratorPage',
    'yaml-json-converter-tool': 'YamlJsonConverterPage',
    'youtube-thumbnail-downloader': 'YoutubeThumbnailPage',
    // Math & Science - Statistics
    't-test-calculator': 'TTestCalculatorPage',
    'chi-square-calculator': 'ChiSquareCalculatorPage',
    'correlation-coefficient-calculator': 'CorrelationCalculatorPage',
    'linear-regression-calculator': 'LinearRegressionCalculatorPage',
    'z-score-calculator': 'ZScoreCalculatorPage',
    'confidence-interval-calculator': 'ConfidenceIntervalCalculatorPage',
    // Math & Science - Algebra
    'permutation-calculator': 'PermutationCalculatorPage',
    'combination-calculator': 'CombinationCalculatorPage',
    'rms-calculator': 'RmsCalculatorPage',
    // Math & Science - Geometry
    'midpoint-calculator': 'MidpointCalculatorPage',
    'slope-calculator': 'SlopeCalculatorPage',
    'circle-calculator': 'CircleCalculatorPage',
    'cylinder-calculator': 'CylinderCalculatorPage',
    'cone-calculator': 'ConeCalculatorPage',
    'sphere-calculator': 'SphereCalculatorPage',
    // Math & Science - Physics & Electronics
    'ohms-law-calculator': 'OhmsLawCalculatorPage',
    'resistor-color-code': 'ResistorColorCodePage',
    'kinetic-energy-calculator': 'KineticEnergyCalculatorPage',
    'force-calculator': 'ForceCalculatorPage',
    'projectile-motion-calculator': 'ProjectileMotionCalculatorPage',
    // NEW Developer Tools - Batch 1
    'json-diff-comparator': 'JsonDiffPage',
    'xml-validator-checker': 'XmlValidatorPage',
    'yaml-validator-checker': 'YamlValidatorPage',
    'toml-json-converter': 'TomlJsonConverterPage',
    // NEW Developer Tools - Batch 2
    'cron-expression-translator': 'CronTranslatorPage',
    'ipv6-expander-compressor': 'Ipv6ExpanderPage',
    'mac-address-generator': 'MacAddressGeneratorPage',
    'http-header-parser-tool': 'HttpHeaderParserPage',
    'url-parser-splitter': 'UrlParserPage',
    'query-string-json-converter': 'QueryStringConverterPage',
    // NEW Developer Tools - Batch 3 (Link Generators)
    'mailto-link-generator': 'MailtoGeneratorPage',
    'youtube-timestamp-link-generator': 'YouTubeGeneratorPage',
    'whatsapp-link-generator': 'WhatsAppGeneratorPage',
    'telegram-link-generator': 'TelegramGeneratorPage',
    // NEW Developer Tools - Batch 4 (Security & SEO)
    'csp-generator': 'CSPGeneratorPage',
    'webmanifest-generator': 'WebmanifestGeneratorPage',
    'permissions-policy-tool': 'PermissionsPolicyPage',
    'meta-tags-analyzer': 'MetaTagsAnalyzerPage',
    // Batch 5.1 (Unicode Text)
    'reverse-text-generator': 'ReverseTextGeneratorPage',
    'upside-down-text-generator': 'UpsideDownTextGeneratorPage',
    'small-text-generator': 'SmallTextGeneratorPage',
    'zalgo-text-generator': 'ZalgoTextGeneratorPage',
    'strikethrough-text-generator': 'StrikethroughTextGeneratorPage',
    'underline-text-generator': 'UnderlineTextGeneratorPage',
    'bold-text-generator': 'BoldTextGeneratorPage',
    'italic-text-generator': 'ItalicTextGeneratorPage',
    'cursive-text-generator': 'CursiveTextGeneratorPage',
    'old-english-text-generator': 'OldEnglishTextGeneratorPage',
    // Batch 5.2 (Ciphers & Utilities)
    'rot13-encoder': 'Rot13EncoderPage',
    'caesar-cipher': 'CaesarCipherPage',
    'atbash-cipher': 'AtbashCipherPage',
    'vigenere-cipher': 'VigenereCipherPage',
    'leet-speak-generator': 'LeetSpeakGeneratorPage',
    'invisible-text-generator': 'InvisibleTextGeneratorPage',
    'random-word-generator': 'RandomWordGeneratorPage',
    'pirate-ipsum-generator': 'PirateIpsumGeneratorPage',
    'palindrome-checker': 'PalindromeCheckerPage',
    'tabs-to-spaces-converter': 'TabsToSpacesConverterPage',
    // Finance Calculators
    'salary-converter': 'SalaryConverterPage',
    'loan-payoff-calculator': 'LoanPayoffCalculatorPage',
    'roi-calculator': 'RoiCalculatorPage',
    'apy-calculator': 'ApyCalculatorPage',
    'break-even-calculator': 'BreakEvenCalculatorPage',
    'capm-calculator': 'CapmCalculatorPage',
    'tv-size-calculator': 'TVSizeCalculatorPage',
    'tvm-calculator': 'TvmCalculatorPage',
    // Unit Converters
    'roman-numeral-converter': 'RomanNumeralConverterPage',
    'temperature-converter': 'TemperatureConverterPage',
    'speed-converter': 'SpeedConverterPage',
    'mass-converter': 'MassConverterPage',
    'length-converter': 'LengthConverterPage',
    'area-converter': 'AreaConverterPage',
    'volume-converter': 'VolumeConverterPage',
    'power-converter': 'PowerConverterPage',
    'energy-converter': 'EnergyConverterPage',
    'pressure-converter': 'PressureConverterPage',
    'torque-converter': 'TorqueConverterPage',
    'angle-converter': 'AngleConverterPage',
    'data-storage-converter': 'DataStorageConverterPage',
    'fuel-efficiency-converter': 'FuelEfficiencyConverterPage',
    // Health & Fitness Calculators
    'body-fat-calculator': 'BodyFatCalculatorPage',
    'lean-body-mass-calculator': 'LeanBodyMassCalculatorPage',
    'ideal-weight-calculator': 'IdealWeightCalculatorPage',
    'pace-calculator': 'PaceCalculatorPage',
    'one-rep-max-calculator': 'OneRepMaxCalculatorPage',
    'water-intake-calculator': 'WaterIntakeCalculatorPage',
    'target-heart-rate-calculator': 'TargetHeartRateCalculatorPage',
    'zodiac-sign-calculator': 'ZodiacSignCalculatorPage',
    'breath-holding-trainer': 'BreathHoldingTrainerPage',
    // Premium Tools
    'ovulation-calculator': 'OvulationCalculatorPage',
    'love-calculator': 'LoveCalculatorPage',
    'color-contrast-checker-tool': 'ColorContrastCheckerPage',
    'svg-pattern-generator-tool': 'SvgPatternGeneratorPage',
    'ascii-art-generator-tool': 'AsciiArtGeneratorPage',
    'svg-blob-generator-tool': 'SvgBlobGeneratorPage',
    'css-filter-generator-tool': 'CssFilterGeneratorPage',
    'css-transform-generator-tool': 'CssTransformGeneratorPage',
    'compound-interest-calculator-tool': 'CompoundInterestCalculatorPage',
    'margin-calculator-tool': 'MarginCalculatorPage',
    'sales-tax-calculator-tool': 'SalesTaxCalculatorPage',
    'color-palette-generator-tool': 'ColorPaletteGeneratorPage',
    'avatar-generator-tool': 'AvatarGeneratorPage',
    'character-counter-tool': 'CharacterCounterPage',
};

/**
 * Convert route path to page component name
 * Handles both standard patterns and special cases
 * @example routeToPageName('/tools/word-counter') => 'WordCounterPage'
 * @example routeToPageName('/tools/css-gradient-generator') => 'GradientGeneratorPage'
 * @example routeToPageName('/tools/dns-propagation-checker') => 'DnsCheckerPage'
 */
export function routeToPageName(route: string): string {
    // Remove /tools/ prefix
    let path = route.replace('/tools/', '');

    // Check for special case mappings first
    if (SPECIAL_ROUTE_MAPPINGS[path]) {
        return SPECIAL_ROUTE_MAPPINGS[path];
    }

    // Check if it's a nested route (e.g., ai/text-generator)
    const parts = path.split('/');
    if (parts.length === 2) {
        // Nested route: handle special cases for nested routes too
        const [folder, toolName] = parts;
        if (SPECIAL_ROUTE_MAPPINGS[toolName]) {
            return `${folder}/${SPECIAL_ROUTE_MAPPINGS[toolName]}`;
        }

        // Strip suffixes for nested routes
        let cleanToolName = toolName;
        const suffixesToRemove = [
            '-converter', '-tool', '-generator', '-maker',
            '-checker', '-tester', '-analyzer', '-calculator',
            '-online', '-free'
        ];

        for (const suffix of suffixesToRemove) {
            if (cleanToolName.endsWith(suffix)) {
                cleanToolName = cleanToolName.slice(0, -suffix.length);
                break;
            }
        }

        return `${folder}/${toPascalCase(cleanToolName)}Page`;
    }

    // Strip common suffixes that appear in routes but not in file names
    const suffixesToRemove = [
        '-converter', '-tool', '-generator', '-maker',
        '-checker', '-tester', '-analyzer', '-calculator',
        '-online', '-free'
    ];

    for (const suffix of suffixesToRemove) {
        if (path.endsWith(suffix)) {
            path = path.slice(0, -suffix.length);
            break; // Only remove one suffix
        }
    }

    // Simple route: word-counter => WordCounterPage
    return `${toPascalCase(path)}Page`;
}

/**
 * Validate that a page name follows the expected convention
 * @example isValidPageName('WordCounterPage') => true
 * @example isValidPageName('wordCounter') => false
 */
export function isValidPageName(pageName: string): boolean {
    // Must end with 'Page'
    if (!pageName.endsWith('Page')) return false;

    // Must be PascalCase (start with uppercase)
    if (pageName[0] !== pageName[0].toUpperCase()) return false;

    return true;
}
